import React, { useState, useCallback } from "react";
import { StyleSheet, View } from "react-native";
import { useFocusEffect } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { useThemeColor } from "@/hooks/useThemeColor";

import { useFirebase } from "@components/firebase/useFirebase";
import { getAllMedia } from "@/services/firebase/media-functions";
import { MediaModel } from "common247";
import { MediaCard } from "@/components/media/MediaCard";
import { MEDIA_FAVOURITE_FILTER, MEDIA_FAVOURITE_GROUP_OPTION, MEDIA_CATEGORY_GROUP_OPTION } from "@/components/media/MediaGallery";

function formatMonthYear(timestamp?: number): string {
  if (!timestamp) return "Unknown Date";
  const dateObj = new Date(timestamp);
  if (isNaN(dateObj.getTime())) return "Unknown Date";
  return dateObj.toLocaleDateString("en-US", { year: "numeric", month: "long" });
}

const MEDIA_SORT_OPTIONS: ISortOption<MediaModel>[] = [
  { key: "date", label: "Date", getValue: (m) => m.date?.formatted || m.mediaDate?.formatted || m.createdAt || 0 },
  { key: "mediaDate", label: "Media Date", getValue: (m) => m.date?.formatted || m.mediaDate?.formatted || m.createdAt || 0 },
  { key: "createdAt", label: "Upload Date", getValue: (m) => m.createdAt || 0 },
  { key: "favouriteRating", label: "Favourite Rating", getValue: (m) => m.favouriteRating ?? 3 },
  { key: "title", label: "Title", getValue: (m) => m.title || "" },
  { key: "contentType", label: "Media Type", getValue: (m) => m.contentType || "" },
  { key: "owner", label: "Owner", getValue: (m) => m.ownerDisplayName || "" },
];

const MEDIA_GROUP_OPTIONS: IGroupOption<MediaModel>[] = [
  MEDIA_CATEGORY_GROUP_OPTION,
  {
    key: "owner",
    label: "Owner",
    getGroupValue: (m) => m.ownerDisplayName || "Unknown Owner",
  },
  MEDIA_FAVOURITE_GROUP_OPTION,
  {
    key: "uploadMonth",
    label: "Upload Month",
    getGroupValue: (m) => formatMonthYear(m.createdAt),
  },
  {
    key: "captureMonth",
    label: "Capture Month",
    getGroupValue: (m) => {
      const ts = m.metadata?.capturedAt || m.createdAt;
      return formatMonthYear(ts);
    },
  },
  {
    key: "type",
    label: "Media Type",
    getGroupValue: (m) => {
      if (!m.contentType) return "Other Files";
      if (m.contentType.startsWith("image/")) return "Photos & Images";
      if (m.contentType.startsWith("video/")) return "Videos";
      if (m.contentType.startsWith("audio/")) return "Audio Recordings";
      return "Other Files";
    },
  },
];

/**
 * Administrative screen displaying all platform media with view, sort, group, and filter capabilities.
 */
export default function MediaAdminScreen() {
  const services = useFirebase();
  const textMuted = useThemeColor({}, "textMuted");

  const [mediaList, setMediaList] = useState<MediaModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (!services) return;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await getAllMedia(services, {});
      if (response.status) {
        setMediaList(response.media || []);
      } else {
        CustomAlert.alert("Error", response.error || "Failed to fetch media.");
      }
    } catch (error) {
      console.error("Error fetching media directory:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching the media directory.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [services]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  return (
    <ThemedScreen title="Media Admin" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 6 }}>
          {`${mediaList.length} TOTAL ${mediaList.length === 1 ? "ITEM" : "ITEMS"}`}
        </ThemedText>

        <ThemedList<MediaModel>
          data={mediaList}
          entityType="media"
          sortOptions={MEDIA_SORT_OPTIONS}
          defaultSortKey="createdAt"
          defaultSortDirection="desc"
          customFilters={[MEDIA_FAVOURITE_FILTER]}
          groupOptions={MEDIA_GROUP_OPTIONS}
          defaultGroupKey="owner"
          persistenceKey="admin-media"
          pageSize={24}
          filterKeys={["title", "contentType", "description", "ownerDisplayName"]}
          filterPlaceholder="Search all media..."
          loading={loading}
          onRefresh={() => fetchData(true)}
          refreshing={refreshing}
          emptyText="No media found in the system."
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, _isHovered, viewMode) => (
            <View style={{ width: (viewMode === "compact" || viewMode === "chip") ? "auto" : "100%", marginBottom: viewMode === "details" ? 1 : viewMode === "small" ? 2 : viewMode === "large" ? 4 : (viewMode === "compact" || viewMode === "chip") ? 0 : 3 }}>
              <MediaCard
                media={item}
                showOwner={true}
                isAdminView={true}
                canEditTags={false}
                viewMode={viewMode}
                onPress={() => {}}
              />
            </View>
          )}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: 24,
  },
});
